/* eslint-disable no-alert */
/* eslint-disable no-restricted-globals */

import FormControl from "@material-ui/core/FormControl";
import RadioGroup from "@material-ui/core/RadioGroup";
import React, { FC, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import AddAdvertiserOption from "./components/AddAdvertiserOption";
import CampaignCreationSlideContainer from "../../../components/CampaignCreation/CampaignCreationSlideContainer";
import RadioButtonOption from "./components/RadioButtonOption";
import classes from "./AdvertiserSelectionSlide.module.scss";
import { BaseStepComponentProps } from "../../../components/modules/BaseStepper/BaseStepper";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { errorToaster } from "../../../helpers/toasters";
import { generateBrandgenCampaignUuid } from "../../../utils/generateBrandgenUuid";
import { keyDownA11y } from "../../../helpers/accessibility";
import {
  setCampaignBasicDetails,
  setCampaignToCreateSelectedAdvertiser,
} from "../../../redux/reducers/campaignToCreate";

const brandgenCampaignId = generateBrandgenCampaignUuid();

const AdvertiserSelectionSlide: FC<BaseStepComponentProps> = ({
  isAttemptingToGoNext,
  setIsAttemptingToGoNext,
  handleNext,
  handleResetSteps,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const location = useLocation();

  const agency = useSelector((state: ReduxApplicationState) => state.agency!);
  const currentUser = useSelector((state: ReduxApplicationState) => state.user.currentUser);
  const campaignToCreateBasicDetails = useSelector(
    (state: ReduxApplicationState) => state.campaignToCreate.basicDetails,
  );

  const { advertisers, agencyId } = agency!;

  const [selectedAdvertiserIndex, setSelectedAdvertiserIndex] = useState<number | null>(null);

  const handleNavigate = useCallback(
    (link: string) => () => {
      history.push(link);
    },
    [history],
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const advertiserIndex = Number(event.target.value);
    setSelectedAdvertiserIndex(advertiserIndex);
    handleResetSteps!();
  };

  useEffect(() => {
    if (isAttemptingToGoNext) {
      if (selectedAdvertiserIndex !== null) {
        const advertiserToSelect = advertisers[selectedAdvertiserIndex as number];

        const isHubspotAuthed = advertiserToSelect.hubspot_authed;
        const havePaymentMethod = advertiserToSelect.paymentMethod?.card;

        if ((!isHubspotAuthed && location.pathname === "/campaigns/create/managed") || !havePaymentMethod) {
          if (!havePaymentMethod) {
            errorToaster(
              <div>
                The selected advertising must have a connected payment method to configure a campaign! Go to the{" "}
                <span
                  onKeyDown={keyDownA11y(handleNavigate(`/advertisers/${advertiserToSelect.brandgenAdvertiserId}`))}
                  onClick={handleNavigate(`/advertisers/${advertiserToSelect.brandgenAdvertiserId}`)}
                  role="link"
                  tabIndex={0}
                  className={classes.spanLink}
                >
                  Advertiser Detail Page
                </span>{" "}
                to complete configuration.
              </div>,
              20000,
            );

            setIsAttemptingToGoNext(false);
            return;
          }

          if (!isHubspotAuthed && location.pathname === "/campaigns/create/managed") {
            if (
              !confirm(
                "The selected advertiser is not connected to a HubSpot account. Do you wish to proceed with a Non-HubSpot ABM Campaign? You will be asked to upload a CSV file with the list of companies you wish to target.",
              )
            ) {
              setIsAttemptingToGoNext(false);
              return;
            }
          }
        }

        dispatch(setCampaignToCreateSelectedAdvertiser(advertiserToSelect));
        dispatch(
          setCampaignBasicDetails({
            ...campaignToCreateBasicDetails,
            agencyId,
            brandgenCampaignId,
            createdBy: currentUser,
          }),
        );
        handleNext();
        setIsAttemptingToGoNext(false);
      } else {
        errorToaster("Please, select advertiser.");
        setIsAttemptingToGoNext(false);
      }
    }
  }, [
    isAttemptingToGoNext,
    selectedAdvertiserIndex,
    setIsAttemptingToGoNext,
    advertisers,
    dispatch,
    campaignToCreateBasicDetails,
    agencyId,
    currentUser,
    handleNext,
    handleNavigate,
    location.pathname,
  ]);

  return (
    <CampaignCreationSlideContainer heading="Select Advertiser">
      <FormControl component="fieldset" className={classes.fieldSet}>
        <RadioGroup
          aria-label="Advertiser Selection"
          className={classes.radioGroup}
          value={selectedAdvertiserIndex}
          onChange={handleChange}
        >
          {advertisers.map((advertiser, index) => (
            <RadioButtonOption key={advertiser.adLibAdvertiserId} label={advertiser.name} value={index} />
          ))}
          <AddAdvertiserOption />
        </RadioGroup>
      </FormControl>
    </CampaignCreationSlideContainer>
  );
};

export default AdvertiserSelectionSlide;
